"use client"

import { useParams } from "next/navigation"
import { useState } from "react"

import { addToCart } from "@lib/data/cart"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type SystemCartItem = {
  variant_id: string
  quantity: number
}

type Status = "idle" | "adding" | "done" | "error"

// One-click "add the whole premium system" button for the /cooling-system
// bundle pages. Adds each line sequentially (radiator first) so the cart is
// built in a sensible order and a mid-sequence failure leaves a resumable
// state instead of a half-random cart. These adds happen outside the PDP, so
// the post-add wizard/upsell never mounts — no suppression needed.
const AddSystemToCartButton = ({ items }: { items: SystemCartItem[] }) => {
  const countryCode = useParams().countryCode as string
  const [status, setStatus] = useState<Status>("idle")
  const [progress, setProgress] = useState(0)
  // Resume point after a failure so retrying never duplicates items that
  // already made it into the cart.
  const [nextIndex, setNextIndex] = useState(0)

  const handleClick = async () => {
    setStatus("adding")

    for (let i = nextIndex; i < items.length; i++) {
      setProgress(i + 1)
      try {
        await addToCart({
          variantId: items[i].variant_id,
          quantity: items[i].quantity,
          countryCode,
        })
        setNextIndex(i + 1)
      } catch {
        setStatus("error")
        return
      }
    }

    setStatus("done")
  }

  if (status === "done") {
    return (
      <div
        className="flex flex-wrap items-center gap-3"
        data-testid="premium-add-system-done"
      >
        <span className="txt-compact-small-plus text-ui-fg-base">
          All {items.length} items are in your cart.
        </span>
        <LocalizedClientLink
          href="/cart"
          className="inline-flex items-center justify-center rounded-md bg-amber-600 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-amber-500"
          data-testid="premium-view-cart-link"
        >
          View Cart
        </LocalizedClientLink>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={status === "adding"}
        className="inline-flex items-center justify-center rounded-md bg-amber-600 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-70"
        data-testid="premium-add-system-button"
      >
        {status === "adding"
          ? `Adding ${progress} of ${items.length}...`
          : status === "error"
            ? "Resume adding the system"
            : "Add Entire System to Cart"}
      </button>
      {status === "error" && (
        <span className="txt-compact-small text-rose-600">
          Something went wrong on item {progress}. Click again to pick up where
          it left off — nothing gets added twice.
        </span>
      )}
    </div>
  )
}

export default AddSystemToCartButton
